import NationalPlaceSelector from '@app/components/NationalPlaceSelector'
import { Address, IAddress } from '@app/models/Address'
import {
  actionCreators as globalActionCreators,
  IGlobalConfigurationState,
  IGlobalPlacesState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { humanize } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import * as React from 'react'
import {
  Button,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  Row,
} from 'reactstrap'

interface IComponentProps {
  cityStateOnly?: boolean
  className?: string
  renderAddressType?: boolean
  style?: React.CSSProperties
  onChange: (newValue: Address) => void
  onRemoveAddressItem?: (index: number) => void
  value: IAddress
}

type Props = IComponentProps &
  IGlobalConfigurationState &
  IGlobalPlacesState &
  typeof globalActionCreators

export default class AddressEditor extends React.PureComponent<Props> {
  public constructor(props: any) {
    super(props)

    bindFunctions(this)
  }

  public render() {
    const address = this.props.value
    const renderType = this.props.renderAddressType || false

    if (this.props.cityStateOnly) {
      return this._renderCityStateComponent(address)
    } else {
      return this._renderFullAddressComponent(address, renderType)
    }
  }

  private _renderCityStateComponent(address: IAddress) {
    return (
      <div>
        <NationalPlaceSelector
          name='CityAndState'
          onChange={this._editCityState}
          onInputChange={this._onCityStateInputChanged}
          required
          value={`${address.city ? `${address.city}, ${address.state}` : ''}`}
        />
      </div>
    )
  }

  private _renderFullAddressComponent(address: IAddress, renderType: boolean) {
    return (
      <div
        className={classNames('d-flex flex-column', this.props.className)}
        style={this.props.style}
      >
        <div className='d-flex flex-row'>
          {renderType && (
            <InputGroup className='mb-2'>
              <Input
                type='select'
                value={address.type}
                onChange={(e) => this._editAddressType(e.target.value)}
              >
                {this.props.configuration.addressTypes
                  .toArray()
                  .map((c, index) => (
                    <option key={`addressType-${index}`} value={c}>
                      {humanize(c)}
                    </option>
                  ))}
              </Input>
              <InputGroupAddon addonType='append'>
                <Button
                  className='align-self-center'
                  color='danger'
                  onClick={() => this._removeAddressItem(address.addressIndex)}
                >
                  <FontAwesomeIcon icon={['fal', 'trash']} fixedWidth />
                </Button>
              </InputGroupAddon>
            </InputGroup>
          )}
        </div>
        <Input
          placeholder='Address Line 1'
          className='mb-2'
          name='AddressLine1'
          required
          value={address.line1}
          onChange={(e) => this._editAddressLine1(e.target.value)}
        />
        <Input
          placeholder='Address Line 2'
          className='mb-2'
          value={address.line2}
          onChange={(e) => this._editAddressLine2(e.target.value)}
        />
        <Row>
          <Col md={8} xl={10}>
            <NationalPlaceSelector
              name='CityAndState'
              onChange={this._editCityState}
              onInputChange={this._onCityStateInputChanged}
              required
              value={
                address.state
                  ? `${address.city}, ${address.state}`
                  : address.city
              }
            />
          </Col>
          <Col>
            <Input
              placeholder='Zip'
              name='Zip'
              required
              value={address.zip}
              onChange={(e) => this._editZipCode(e.target.value)}
            />
          </Col>
        </Row>
      </div>
    )
  }

  private _editAddressType(value: string) {
    const newValue = (this.props.value as Address).set('type', value) as Address
    this.props.onChange(newValue)
  }

  private _editAddressLine1(value: string) {
    const newValue = (this.props.value as Address).set(
      'line1',
      value,
    ) as Address
    this.props.onChange(newValue)
  }

  private _editAddressLine2(value: string) {
    const newValue = (this.props.value as Address).set(
      'line2',
      value,
    ) as Address
    this.props.onChange(newValue)
  }

  private _editCityState(value?: any) {
    const newValue = (this.props.value as Address).withMutations((addr) => {
      addr
        .set('city', value ? value.placeName : '')
        .set('state', value ? value.state : '')
        .set('placeFIPS', value ? value.placeFIPS : '')
        .set('stateFIPS', value ? value.stateFIPS : '')
    }) as Address
    this.props.onChange(newValue)
  }

  private _editZipCode(value: string) {
    const newValue = (this.props.value as Address).set('zip', value) as Address
    this.props.onChange(newValue)
  }

  private _removeAddressItem(index: number) {
    if (this.props.onRemoveAddressItem) {
      this.props.onRemoveAddressItem(index)
    }
  }

  private _onCityStateInputChanged(input: string) {
    const newValue = (this.props.value as Address)
      .set('city', input)
      .set('state', '') as Address
    this.props.onChange(newValue)
  }
}
